import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type POStatus = 'draft' | 'pending' | 'approved' | 'ordered' | 'partial' | 'received' | 'cancelled';

export interface PurchaseOrderItem {
  id: string;
  inventoryItemId: string | null;
  itemName: string;
  partNumber: string | null;
  quantityOrdered: number;
  quantityReceived: number;
  /** Nulo para quem não vê preço de compra (sem estoque.custo, compras.pedir e compras.aprovar). */
  unitPrice: number | null;
  totalPrice: number | null;
  notes: string | null;
}

export interface PurchaseOrder {
  id: string;
  number: string;
  supplierId: string;
  supplierName?: string;
  status: POStatus;
  totalAmount: number | null;
  itemsCount: number;
  expectedDelivery: string | null;
  notes: string | null;
  createdById: string;
  createdByName?: string;
  approvedById: string | null;
  approvedAt: string | null;
  orderedAt: string | null;
  receivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  items?: PurchaseOrderItem[];
}

interface PurchaseOrdersFilters {
  status?: POStatus;
  supplierId?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

interface PaginatedResponse {
  orders: PurchaseOrder[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export function usePurchaseOrders(filters: PurchaseOrdersFilters = {}) {
  const queryClient = useQueryClient();

  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.supplierId) params.set('supplierId', filters.supplierId);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));
  const qs = params.toString();

  const ordersQuery = useQuery<PaginatedResponse>({
    queryKey: ['purchase-orders', filters],
    queryFn: async () => {
      const res = await api.get(`/purchase-orders${qs ? `?${qs}` : ''}`);
      return res.data;
    },
  });

  const invalidatePO = () => {
    queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    queryClient.invalidateQueries({ queryKey: ['purchase-order'] });
  };

  const createMutation = useMutation({
    mutationFn: async (data: {
      supplierId: string;
      items: {
        inventoryItemId?: string | null;
        itemName: string;
        partNumber?: string | null;
        quantityOrdered: number;
        unitPrice: number;
        notes?: string | null;
      }[];
      expectedDelivery?: string | null;
      notes?: string | null;
    }) => {
      const res = await api.post('/purchase-orders', data);
      return res.data;
    },
    onSuccess: invalidatePO,
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: {
      id: string;
      supplierId?: string;
      items?: {
        inventoryItemId?: string | null;
        itemName: string;
        partNumber?: string | null;
        quantityOrdered: number;
        unitPrice: number;
        notes?: string | null;
      }[];
      expectedDelivery?: string | null;
      notes?: string | null;
    }) => {
      const res = await api.patch(`/purchase-orders/${id}`, data);
      return res.data;
    },
    onSuccess: invalidatePO,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete(`/purchase-orders/${id}`);
      return res.data;
    },
    onSuccess: invalidatePO,
  });

  const submitMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/purchase-orders/${id}/submit`);
      return res.data;
    },
    onSuccess: invalidatePO,
  });

  const approveMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/purchase-orders/${id}/approve`);
      return res.data;
    },
    onSuccess: invalidatePO,
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const res = await api.post(`/purchase-orders/${id}/reject`, { reason });
      return res.data;
    },
    onSuccess: invalidatePO,
  });

  const confirmOrderMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/purchase-orders/${id}/confirm-order`);
      return res.data;
    },
    onSuccess: invalidatePO,
  });

  const receiveMutation = useMutation({
    mutationFn: async ({ id, ...data }: {
      id: string;
      items: {
        purchaseOrderItemId: string;
        quantityReceived: number;
        /** Sem ele, vale o preço do pedido (quem recebe sem ver custo). */
        unitCost?: number;
      }[];
      notes?: string | null;
    }) => {
      const res = await api.post(`/purchase-orders/${id}/receive`, data);
      return res.data;
    },
    onSuccess: () => {
      invalidatePO();
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-alerts'] });
    },
  });

  const standaloneReceiveMutation = useMutation({
    mutationFn: async (data: {
      items: {
        inventoryItemId: string;
        quantityReceived: number;
        unitCost: number;
        notes?: string | null;
      }[];
      notes?: string | null;
    }) => {
      const res = await api.post('/inventory/receive-standalone', data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-alerts'] });
    },
  });

  return {
    orders: ordersQuery.data?.orders ?? [],
    total: ordersQuery.data?.total ?? 0,
    page: ordersQuery.data?.page ?? 1,
    totalPages: ordersQuery.data?.totalPages ?? 1,
    isLoading: ordersQuery.isLoading,
    error: ordersQuery.error,
    isError: ordersQuery.isError,
    refetch: ordersQuery.refetch,
    create: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    update: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    remove: deleteMutation.mutateAsync,
    submit: submitMutation.mutateAsync,
    isSubmitting: submitMutation.isPending,
    approve: approveMutation.mutateAsync,
    isApproving: approveMutation.isPending,
    reject: rejectMutation.mutateAsync,
    isRejecting: rejectMutation.isPending,
    confirmOrder: confirmOrderMutation.mutateAsync,
    isConfirming: confirmOrderMutation.isPending,
    receive: receiveMutation.mutateAsync,
    isReceiving: receiveMutation.isPending,
    standaloneReceive: standaloneReceiveMutation.mutateAsync,
    isStandaloneReceiving: standaloneReceiveMutation.isPending,
  };
}

export function usePurchaseOrder(id: string | null) {
  return useQuery<{ order: PurchaseOrder }>({
    queryKey: ['purchase-order', id],
    queryFn: async () => {
      const raw = (await api.get(`/purchase-orders/${id}`)).data;
      const items: PurchaseOrderItem[] = (raw.items ?? []).map((i: Record<string, unknown>) => ({
        id: i.id as string,
        inventoryItemId: (i.itemId ?? i.inventoryItemId ?? null) as string | null,
        itemName: i.itemName as string,
        partNumber: (i.partNumber ?? null) as string | null,
        quantityOrdered: i.quantityOrdered as number,
        quantityReceived: (i.quantityReceived ?? 0) as number,
        unitPrice: i.unitPrice == null ? null : Number(i.unitPrice),
        totalPrice: i.unitPrice == null ? null : Number(i.totalPrice ?? (Number(i.quantityOrdered ?? 0) * Number(i.unitPrice))),
        notes: (i.notes ?? null) as string | null,
      }));
      return {
        order: {
          ...raw,
          createdById: raw.createdById ?? raw.createdBy ?? null,
          approvedById: raw.approvedById ?? raw.approvedBy ?? null,
          itemsCount: raw.itemsCount ?? items.length,
          items,
        } as PurchaseOrder,
      };
    },
    enabled: !!id,
  });
}
